import { ref, onUnmounted, type Ref } from 'vue';
import { io, type Socket } from 'socket.io-client';

export type ConnectionStatus = 'disconnected' | 'connecting' | 'connected' | 'error';

export interface SocketEvent {
  id: number;
  eventName: string;
  data: unknown;
  receivedAt: Date;
}

interface UseSocketIOReturn {
  status: Ref<ConnectionStatus>;
  events: Ref<SocketEvent[]>;
  errorMessage: Ref<string>;
  connect: () => void;
  disconnect: () => void;
  clearEvents: () => void;
}

const MAX_EVENTS = 500;
let eventCounter = 0;

export function useSocketIO(): UseSocketIOReturn {
  const status = ref<ConnectionStatus>('disconnected');
  const events = ref<SocketEvent[]>([]);
  const errorMessage = ref('');
  let socket: Socket | null = null;

  const connect = () => {
    if (socket) return;

    status.value = 'connecting';
    errorMessage.value = '';

    const baseUrl = import.meta.env.VITE_API_BASE_URL?.replace('/api', '') ?? 'http://localhost:3000';

    socket = io(baseUrl, {
      path: '/api/socket.io',
      withCredentials: true,
      transports: ['websocket', 'polling'],
    });

    socket.on('connect', () => {
      status.value = 'connected';
      errorMessage.value = '';
    });

    socket.on('disconnect', (reason) => {
      status.value = 'disconnected';
      errorMessage.value = reason === 'io server disconnect'
        ? 'Servidor encerrou a conexão'
        : '';
    });

    socket.on('connect_error', (err) => {
      status.value = 'error';
      errorMessage.value = err.message;
    });

    socket.onAny((eventName: string, data: unknown) => {
      events.value.unshift({
        id: ++eventCounter,
        eventName,
        data,
        receivedAt: new Date(),
      });
      if (events.value.length > MAX_EVENTS) {
        events.value = events.value.slice(0, MAX_EVENTS);
      }
    });
  };

  const disconnect = () => {
    socket?.disconnect();
    socket = null;
    status.value = 'disconnected';
    errorMessage.value = '';
  };

  const clearEvents = () => {
    events.value = [];
  };

  onUnmounted(() => {
    disconnect();
  });

  return { status, events, errorMessage, connect, disconnect, clearEvents };
}
